<template>
  <DefineFooterColumn v-slot="{ title, items }">
    <div>
      <h3 v-if="title" class="text-base font-semibold tracking-wide">
        {{ title }}
      </h3>
      <ul class="mt-4 space-y-4">
        <li v-for="{ label, to, target } in items" :key="label">
          <NuxtLink
            :to
            :target
            class="text-base font-medium leading-6 hover:underline"
          >
            {{ label }}
          </NuxtLink>
        </li>
      </ul>
    </div>
  </DefineFooterColumn>

  <footer class="bg-muted flex-none" aria-labelledby="footer-heading">
    <h2 id="footer-heading" class="sr-only">Footer</h2>
    <div class="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <!-- MARK: Desktop -->
      <div class="hidden md:grid gap-y-12 gap-x-4 grid-cols-3 lg:grid-cols-4">
        <ReuseFooterColumn
          v-for="item in navigation"
          :key="item.key"
          :title="item.label"
          :items="item.items"
        />

        <div
          class="col-span-2 row-start-3 text-left lg:col-span-1 md:col-span-3 md:row-start-auto"
        >
          <h3 class="text-sm font-semibold tracking-wider uppercase">
            {{ $t(`theFooter.name`) }}
          </h3>

          <div class="mt-4 space-y-4 text-base leading-6">
            <p>{{ $t(`theFooter.address`) }}</p>
          </div>
        </div>
      </div>

      <!-- MARK: Mobile -->
      <UAccordion
        :items="navigation"
        :ui="{
          content: 'pb-4',
          trigger: 'text-base font-semibold tracking-wide',
          trailingIcon: 'text-primary',
        }"
        class="md:hidden"
      >
        <template #content="{ item }">
          <ReuseFooterColumn :items="item.items" />
        </template>
      </UAccordion>
    </div>

    <div class="py-8 px-4 m-auto sm:px-6 lg:px-8 max-w-7xl">
      <div class="md:flex items-center justify-between">
        <div class="md:flex items-center content-center mt-8 gap-2 md:mt-0">
          <Icon name="i-custom-logo" class="hidden md:block size-6" />
          <Icon
            name="i-custom-logo-text-dark"
            alt="checkandrent.com"
            class="md:hidden w-64 h-7 mb-5"
          />

          <p class="text-muted text-xs md:text-sm font-normal md:mt-0">
            {{ $t('theFooter.copyright') }}
          </p>
        </div>

        <ul
          class="mt-5 flex justify-between items-center gap-x-6 max-w-sm md:order-2 md:mt-0"
        >
          <li
            v-for="{ icon, label, to, target } in socialDomainLinks"
            :key="label"
            class="text-muted hover:text-primary"
          >
            <NuxtLink :to :target>
              <span class="sr-only">{{ label }}</span>
              <Icon :name="icon!" class="size-5" aria-hidden="true" />
            </NuxtLink>
          </li>
        </ul>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import type { AccordionItem, NavigationMenuItem } from '@nuxt/ui'
import {
  companyDomainLinks,
  legalDomainLinks,
  socialDomainLinks,
  vehicleDomainLinks,
} from '@/constants/navigationLinks'

const { t } = useI18n()
const [DefineFooterColumn, ReuseFooterColumn] = createReusableTemplate<{
  items: NavigationMenuItem[]
  title?: string
}>()

const navigation = computed<
  (AccordionItem & { items: NavigationMenuItem[] })[]
>(() => [
  {
    key: 'vehicles',
    label: t(`vehicle`, 2),
    items: vehicleDomainLinks,
  },
  {
    key: 'company',
    label: t(`theFooter.company`),
    items: companyDomainLinks,
  },
  {
    key: 'legal',
    label: t(`theFooter.legal`),
    items: legalDomainLinks,
  },
])
</script>
